<template>
  <NuxtLayout :name="setLayout">
    <div class="error_page pb-12">
      <div class="flex flex-wrap -mx-4 error_page_content mt-4">
        <LazyNotFoundError v-if="error.statusCode === 404" />

        <LazyServerError v-else />

        <div class="md:w-12/12 px-0 mt-12">
          <LazyAlgoliaSearchBar class="mb-4" />
          <LazyTrips
            v-if="!$isSolWebsite"
          />
        </div>
      </div>
    </div>
  </NuxtLayout>
</template>

<script setup>
import { LocaleApiService } from '@layers/web/services/locale-api.ts'

const props = defineProps({
  error: {
    type: Object,
    required: true,
  },
})

const rootStore = useRootStore()
const menuStore = useMenuStore()

const { setPageBanner } = useBanner()

const { $isSolWebsite, $sentryCaptureMessage, $locale, $i18n, $t } = useNuxtApp()

const setLayout = computed(() => isSolWebsite() ? 'charter-page' : 'banner-default')

await useAsyncData(async () => {
  const [generalLocale] = await Promise.all([
    LocaleApiService.fetchLocalTranslations(),
    ...($isSolWebsite ? [] : [rootStore.fetchTripTypesSidebar()]),
    rootStore.fetchTopNavbar(),
    rootStore.fetchAlert(),
  ])

  $i18n.$helper.loadTranslations($locale, generalLocale, false)

  return {
    generalLocale,
  }
})

const handleStatusCode = () => {
  if (props.error.statusCode === 404) {
    setPageBanner({
      title: $t('404oops'),
      subtitle: $t('404subtitle'),
      size: 'small',
    })
    return
  }

  setPageBanner({
    title: $t('500title'),
    titleI18n: '500title',
    subtitle: $t('500subtitle'),
    subtitleI18n: '500subtitle',
    size: 'small',
  })

  const errorMessage = props.error.message || props.error.cause?.message
  $sentryCaptureMessage(
    `${props.error.statusCode} page` + (errorMessage ? `: ${errorMessage}` : ''),
    {
      level: 'error',
      extra: props.error,
    }
  )
}

handleStatusCode()
menuStore.closeSideMenuActive()

/**
 * Title logic is here to prevent the case of client chunk errors affecting search engine result titles.
 * If the error occurs on the client and isn't 404: keep the title from before the error
 */
useHead({
  title: () => {
    if (props.error.statusCode === 404) {
      return $t('404title')
    }

    if (import.meta.client) {
      return document.title
    }

    return $t('500title')
  },

  titleTemplate: (props.error.statusCode !== 404 && import.meta.client)
    ? null
    : undefined,

  meta: [{ name: 'robots', content: 'noindex, follow' }],
})
</script>
